import type { Phase } from "@/models/phases.model"
import { queryOptions } from "@tanstack/react-query"
import { languageParser } from "@/mappers/language.mapper"
import { organisationService } from "@/services/organisations.service"
import { participantsService } from "@/services/participants/participants"
import { competitionService } from "@/services/settings/competition"
import { competitionStyleService } from "@/services/settings/competition-styles"
import { disciplineSettingsService } from "@/services/settings/discipline-settings.service"
import { disciplineService } from "@/services/settings/disciplines"
import { medalsService } from "@/services/settings/medals/medals"
import { phasesService } from "@/services/settings/phases-service"
import { unitsService } from "@/services/settings/units"
import { startListService } from "@/services/startLists/start-list-service"
import { venuesService } from "@/services/venues.service"

export const DisciplineQueryOptions = {
  all: queryOptions({
    queryKey: ["disciplines"],
    queryFn: async () => disciplineService.getAll(),
  }),
  allSettings: queryOptions({
    queryKey: ["disciplines", "settings"],
    queryFn: async () => disciplineSettingsService.getAll(),
  }),
  byCompetitionCode: (disciplineCode: string) => queryOptions({
    queryKey: ["disciplines", "by-competition", disciplineCode],
    queryFn: async () => {
      const settings = await disciplineSettingsService.getByDisciplineCode(disciplineCode)
      return settings
    },
  }),
  allDisciplines: () => queryOptions({
    queryKey: ["disciplines", "all"],
    queryFn: async () => {
      const disciplines = await disciplineService.getAll()
      return disciplines
    },
  }),
  byCode: (disciplineCode: string) => queryOptions({
    queryKey: ["discipline", disciplineCode],
    queryFn: async () => {
      const discipline = await disciplineService.getByCode(disciplineCode)
      return discipline
    },
  }),
}

export const EventQueryOptions = {
  all: queryOptions({
    queryKey: ["events", "all"],
    queryFn: async () => {
      const events = await competitionService.getAllCompetitions()
      return events
    },
  }),
  byCode: (disciplineCode: string) => queryOptions({
    queryKey: ["events", disciplineCode],
    queryFn: async () => {
      const allCompetitions = await competitionService.getAllCompetitionsByDiscipline(disciplineCode)
      const uniqueCompetitions: typeof allCompetitions = []
      const seenCodes = new Set<string>()

      if (Array.isArray(allCompetitions)) {
        for (const comp of allCompetitions) {
          if (comp.metadata.discipline === disciplineCode) {
            if (comp.code && !seenCodes.has(comp.code)) {
              uniqueCompetitions.push(comp)
              seenCodes.add(comp.code)
            }
            else if (!comp.code) {
              console.warn("Competition without code found and matches discipline:", comp)
            }
          }
        }
      }
      return uniqueCompetitions
    },
    enabled: !!disciplineCode,
  }),
  getCompetitionStyles: () => queryOptions({
    queryKey: ["competition-styles"],
    queryFn: async () => {
      const competitionStyles = await competitionStyleService.getCompetitionStyles()
      return competitionStyles
    },
  }),
  getCategories: (disciplineCode: string) => queryOptions({
    queryKey: ["categories", disciplineCode],
    queryFn: async () => {
      const categories = await competitionStyleService.getCategories(disciplineCode)
      return categories
    },
  }),
}

export const CompetitionQueryOptions = {
  getByRscCode: (rscCode: string) => ({
    queryKey: ["competition", "rsc", rscCode],
    queryFn: async () => {
      if (!rscCode)
        return null
      return competitionService.getCompetitionByRscCode(rscCode)
    },
    enabled: !!rscCode,
  }),
}

export const PhaseQueryOptions = {
  byCode: (eventCode: string) => queryOptions({
    queryKey: ["phases", eventCode],
    queryFn: async () => {
      const response = await phasesService.getPhasesByCopmpetitionCode(eventCode)
      // Filter out phases with duplicate codes
      const uniquePhases: Phase[] = []
      const seenCodes = new Set<string>()
      if (Array.isArray(response)) {
        response.forEach((phase: Phase) => {
          if (phase.code && !seenCodes.has(phase.code)) {
            uniquePhases.push(phase)
            seenCodes.add(phase.code)
          }
          else if (!phase.code) {
            console.warn("Phase without code found:", phase)
          }
        })
      }
      return uniquePhases
    },
    enabled: !!eventCode,
  }),
  getSettingsByCompetitionCode: (eventCode: string) => queryOptions({
    queryKey: ["phases", "settings", eventCode],
    queryFn: async () => {
      const response = await phasesService.getSettingsByCompetitionCode(eventCode)

      const res = {
        ...response,
        venues: response.venues?.map(v => ({
          ...v,
          description: languageParser(v.description),
        })),
      }
      return res
    },
    enabled: !!eventCode,
  }),
}

export const UnitQueryOptions = {
  byCode: (phaseCode: string) => queryOptions({
    queryKey: ["units", phaseCode],
    queryFn: async () => {
      const response = await unitsService.getUnitsByPhaseCode(phaseCode)
      // Filter out units with duplicate codes
      const uniqueUnits: typeof response = []
      const seenCodes = new Set<string>()
      if (Array.isArray(response)) {
        response.forEach((unit) => {
          if (unit.code && !seenCodes.has(unit.code)) {
            uniqueUnits.push(unit)
            seenCodes.add(unit.code)
          }
          else if (!unit.code) {
            console.warn("Unit without code found:", unit)
          }
        })
      }
      return uniqueUnits
    },
    enabled: !!phaseCode,
  }),
  all: () => queryOptions({
    queryKey: ["units", "all"],
    queryFn: async () => {
      const response = await unitsService.fetchAllUnits()
      // Filter out units with duplicate codes
      const uniqueUnits: typeof response = []
      const seenCodes = new Set<string>()
      if (Array.isArray(response)) {
        response.forEach((unit) => {
          if (unit.code && !seenCodes.has(unit.code)) {
            uniqueUnits.push(unit)
            seenCodes.add(unit.code)
          }
          else if (!unit.code) {
            console.warn("Unit without code found:", unit)
          }
        })
      }
      return uniqueUnits
    },
  }),
  byUnitCode: (unitCode: string) => queryOptions({
    queryKey: ["units", unitCode],
    queryFn: async () => {
      const response = await unitsService.getUnitsByUnitCode(unitCode)

      return response
    },
    enabled: !!unitCode,
  }),
  getSettingsByPhaseCode: (phaseCode: string) => queryOptions({
    queryKey: ["phases", "settings", phaseCode],
    queryFn: async () => {
      const response = await unitsService.getSettingsByPhaseCode(phaseCode)
      const res = {
        ...response,
        venues: response.venues?.map(v => ({
          ...v,
          description: languageParser(v.description),
        })),
      }
      return res
    },
    enabled: !!phaseCode,
  }),
  getBySportEvent: (eventCode: string) => ({
    queryKey: ["units", "by-event", eventCode],
    queryFn: async () => {
      const response = await unitsService.fetchUnitsByCompetitionCode(eventCode)
      return response
    },
    initialData: [],
    enabled: !!eventCode,
  }),
  getByDisciplineCode: (disciplineCode: string) => ({
    queryKey: ["units", "by-discipline", disciplineCode],
    queryFn: async () => {
      const response = await unitsService.getUnitByDisciplineCode(disciplineCode)
      return response
    },
    enabled: !!disciplineCode,
  }),
}

export const StartListQueryOptions = {
  all: () => queryOptions({
    queryKey: ["start-list", "all"],
    queryFn: async () => {
      const response = await startListService.fetchStartList()
      return response
    },
  }),
  byEventCode: (eventCode: string) => queryOptions({
    queryKey: ["start-list-by-event", "all", eventCode],
    queryFn: async () => {
      const response = await unitsService.fetchUnitsByCompetitionCode(eventCode)
      return response
    },
    enabled: () => !!eventCode,
  }),
  byUnitCode: (unitCode: string | null) => queryOptions({
    queryKey: ["start-list", unitCode],
    queryFn: async () => {
      const response = await startListService.fetchStartListByCode(unitCode || "")

      return response
    },
    enabled: !!unitCode,
  }),
  allParticipantsByEventCode: (eventCode: string) => queryOptions({
    queryKey: ["participants", eventCode],
    queryFn: async () => {
      const response = await participantsService.getBySportEvent(eventCode)
      return response
    },
    enabled: !!eventCode,
  }),
  byPhaseCode: (phaseCode: string | null) => queryOptions({
    queryKey: ["start-list-by-phase", phaseCode],
    queryFn: async () => {
      const response = await startListService.fetchStartListByPhaseCode(phaseCode)
      return response
    },
    enabled: !!phaseCode,
  }),
  getPreviousWinnersbyUnitCode: (unitCode: string) => queryOptions({
    queryKey: ["start-list-by-unit", unitCode],
    queryFn: async () => {
      return startListService.fetchStartListWinners(unitCode)
    },
    enabled: !!unitCode,
  }),
  byDisciplineCode: (disciplineCode: string) => queryOptions({
    queryKey: ["start-list-by-discipline", disciplineCode],
    queryFn: async () => {
      return startListService.fetchStartListByDisciplineCode(disciplineCode)
    },
    enabled: !!disciplineCode,
  }),
}

export const VenueQueryOptions = {
  all: queryOptions({
    queryKey: ["venues"],
    queryFn: async () => {
      const response = await venuesService.fetchVenues()

      return response
    },
  }),
}

export const MedalsQueryOptions = {
  all: () => queryOptions({
    queryKey: ["medals"],
    queryFn: async () => {
      const response = await medalsService.getAllMedals()
      return response
    },
  }),
}

export const OrganisationQueryOptions = {
  all: queryOptions({
    queryKey: ["organisations"],
    queryFn: async () => {
      const response = await organisationService.getAllOrganisations()
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data || []
    },
  }),
}
